import React, { memo, useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import DashboardLayout from '../../layouts/DashboardLayout/DashboardLayout'
import { subStringAvatar } from '../../components/Common/CellText'
import {
  Avatar,
  Button,
  Empty,
  message,
  Popconfirm,
  Space,
  Spin,
  Tooltip,
} from 'antd'
import { inject, observer } from 'mobx-react'
import { apiUrl } from '../../config'
import {
  DeleteOutlined,
  EditOutlined,
  MailOutlined,
  OrderedListOutlined,
  PhoneOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import CreatePositonModal from './CreatePositionModal'
import { find } from 'lodash-es'
import { MODAL_MODE } from '../../ui_constants'
import roleStore from '../../stores/roleStore'
import SortPositionodal from './SortPositionModal/SortPositionModal'
import {
  ACL_ACTION_TYPE,
  MODULE_CODE,
} from '../../constants'
import AccessControlAction from '../../components/AccessControlAction/AccessControlAction'
import UserOverview from '../UserProfilePage/UserOverview'
import useCheckPageAccess from '../../components/AccessPageAction/CheckPageAccess'
import useCheckPageAccessModule from '../../components/AccessPageAction/CheckPageAccessModule'
import AppPageHeader from '../../components/AppPageHeader'
import AppPagination from '../../components/Common/AppPagination'
import { AppPaginationWrapper } from '../../components/Common/AppPaginationStyled'
import {
  EmptyUserWrapper,
  ListPositionContent,
  PositionActionButton,
  PositionActionGroup,
  PositionButton,
  PositionContentWrapper,
  PositionHeader,
  PositionLayout,
  PositionList,
  PositionNav,
  PositionPanel,
  PositionPanelHeader,
  PositionTitle,
  PositionTitleWrapper,
  UserCard,
  UserContactRow,
  UserContactText,
  UserGrid,
  UserMeta,
  UserName,
  UserPosition,
  UserStatusBadge,
} from './PositionStyled'

const getUserInitialName = user => {
  const name = user?.name_lowercase || user?.name_uppercase || user?.name
  return subStringAvatar(name || user?.username)
}

const PositionUserCard = memo(({ user, onView }) => {
  const displayName = user?.name_lowercase || user?.name_uppercase || user?.name
  const positionName = user?.position?.name || 'Chưa cập nhật'
  const statusText = user?.status ? 'Đang hoạt động' : 'Dừng hoạt động'
  const userLabel = displayName || user?.username || ''

  const handleClick = useCallback(() => {
    onView?.(user)
  }, [onView, user])

  const handleKeyDown = useCallback(event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleClick()
    }
  }, [handleClick])

  return (
    <UserCard
      role="button"
      tabIndex={0}
      aria-label={`Xem chi tiết ${userLabel}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}>
      <Avatar
        className="card-avatar"
        size={76}
        src={user?.imageId && `${apiUrl}/api/v1/images/${user.imageId}`}>
        {getUserInitialName(user)}
      </Avatar>
      <Tooltip title={displayName}>
        <UserName>{displayName || 'Chưa cập nhật'}</UserName>
      </Tooltip>
      <Tooltip title={positionName}>
        <UserPosition>{positionName}</UserPosition>
      </Tooltip>
      <UserStatusBadge active={user?.status}>{statusText}</UserStatusBadge>
      <UserMeta>
        <UserContactRow>
          <MailOutlined />
          <Tooltip title={user?.email}>
            <UserContactText>{user?.email || 'Chưa cập nhật'}</UserContactText>
          </Tooltip>
        </UserContactRow>
        <UserContactRow>
          <PhoneOutlined />
          <Tooltip title={user?.phone}>
            <UserContactText>{user?.phone || 'Chưa cập nhật'}</UserContactText>
          </Tooltip>
        </UserContactRow>
      </UserMeta>
    </UserCard>
  )
})

const PositionPage = positionPageProps => {
  const {
    positionStore,
    userStore,
    loadingAnimationStore,
    history,
    aclStore,
    authenticationStore,
    moduleStore,
  } = positionPageProps

  const { positionList } = positionStore

  const {
    userOfPositionListTotalCount,
    userOfPositionListPageIndex,
    userOfPositionListPageSize,
  } = userStore
  const { aclActionsByUser } = aclStore
  const { isAccountAdmin } = authenticationStore
  const { moduleList } = moduleStore

  const [userOfPositionData, setUserOfPositionData] = useState([])
  const [currentPosition, setCurrentPosition] = useState()

  //create/edit Position modal
  const [
    isVisibleCreatePositionModal,
    setIsVisibleCreatePositionModal,
  ] = useState(false)
  const [mode, setMode] = useState(null)

  //edit user
  const [isVisibleUser, setIsVisibleUser] = useState(false)
  //sort Position
  const [isVisibleSortPositionModal, setIsVisibleSortPositionModal] = useState(
    false
  )
  // delete position confirm popover
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false)

  // Check page access module
  useCheckPageAccessModule({
    history,
    moduleList,
    actionCode: MODULE_CODE.quan_tri,
    childActionCode: MODULE_CODE.chuc_danh,
  })

  // Check page access
  useCheckPageAccess({
    history,
    aclActionsByUser,
    isAccountAdmin,
    actionCode: ACL_ACTION_TYPE.menu__CHUC_DANH,
  })

  const handleViewUserInfo = useCallback(
    userInfo => {
      userStore.setSelectedUser(userInfo)
      setIsVisibleUser(true)
      userStore.clearAvatarByUserCode?.()
    },
    [userStore]
  )

  const showEditPositionModal = useCallback(() => {
    setIsVisibleCreatePositionModal(true)
    setMode(MODAL_MODE.UPDATE)
  }, [])

  const loadUserList = useCallback(async () => {
    let response = await userStore.getUserListByPosition()
    setUserOfPositionData(response.data)
  }, [userStore])

  const onChangeTabPosition = useCallback(async key => {
    if (currentPosition?.code === key) return
    try {
      loadingAnimationStore.setTableLoading(true)
      userStore.setFilter('userOfPositionListByPositionCode', key)
      userStore.setFilter('userOfPositionListPageIndex', 0)
      await loadUserList()

      setCurrentPosition(find(positionList, { code: key }))
    } catch (e) {
      message.error(e.vi || 'Đã có lỗi xảy ra')
    } finally {
      loadingAnimationStore.setTableLoading(false)
    }
  }, [
    currentPosition?.code,
    loadUserList,
    loadingAnimationStore,
    positionList,
    userStore,
  ])

  const onChangePagination = useCallback(async (pageIndex, pageSize) => {
    try {
      loadingAnimationStore.setTableLoading(true)

      userStore.setFilter('userOfPositionListPageIndex', pageIndex - 1)
      userStore.setFilter('userOfPositionListPageSize', pageSize)

      let response = await userStore.getUserListByPosition()
      setUserOfPositionData(response.data)
    } catch (e) {
      message.warning('Lỗi lấy thông tin người dùng trong chức danh!')
    } finally {
      loadingAnimationStore.setTableLoading(false)
    }
  }, [loadingAnimationStore, userStore])

  const handleDelete = useCallback(async () => {
    if (userOfPositionListTotalCount === 0) {
      try {
        loadingAnimationStore.showSpinner(true)
        await positionStore.delete(currentPosition.code)

        await positionStore.getPositionList()
        let positionListUpdate = positionStore.positionList
        if (positionListUpdate.length > 0) {
          onChangeTabPosition(positionListUpdate[0].code)
        }
        message.success('Xóa chức danh thành công')
      } catch (e) {
        message.error(e.vi || 'Đã có sự cố xảy ra')
      } finally {
        loadingAnimationStore.showSpinner(false)
      }
    } else {
      message.warning('Không thể xóa chức danh đã có nhân viên')
    }
  }, [
    currentPosition?.code,
    loadingAnimationStore,
    onChangeTabPosition,
    positionStore,
    userOfPositionListTotalCount,
  ])

  const showCreatePositionModal = useCallback(() => {
    setIsVisibleCreatePositionModal(true)
    setMode(MODAL_MODE.CREATE)
  }, [])

  const handleCloseCreatePositionModal = useCallback(() => {
    setIsVisibleCreatePositionModal(false)
    setMode(null)
  }, [])

  const handleCancelViewUser = useCallback(() => {
    setIsVisibleUser(false)
  }, [])

  const showSortPositionModal = useCallback(() => {
    setIsVisibleSortPositionModal(true)
  }, [])

  const handleCancelSortPositionModal = useCallback(() => {
    setIsVisibleSortPositionModal(false)
  }, [])

  const onRefreshListPosition = useCallback(async () => {
    loadingAnimationStore.showSpinner(true)
    await positionStore.getPositionList(
      authenticationStore.currentCompanyCode()
    )
    loadingAnimationStore.showSpinner(false)
  }, [authenticationStore, loadingAnimationStore, positionStore])

  useEffect(() => {
    if (authenticationStore.currentUser) {
      loadingAnimationStore.showSpinner(true)
      Promise.all([
        //for create user account
        positionStore.getPositionList(),
        roleStore.getRoles(),
      ])
        .then(async responses => {
          let firstDept = responses[0].data[0]
          if (!firstDept) {
            setCurrentPosition(undefined)
            setUserOfPositionData([])
            return
          }
          setCurrentPosition(firstDept)

          userStore.setFilter(
            'userOfPositionListByPositionCode',
            firstDept.code
          )
          let response = await userStore.getUserListByPosition()
          setUserOfPositionData(response.data)
        })
        .catch(() => {
          message.warning('Lỗi lấy thông tin chức danh!').then()
        })
        .finally(() => {
          loadingAnimationStore.showSpinner(false)
        })
    }

    return () => {
      positionStore.clearPositionList()
    }
  }, [authenticationStore.currentUser])

  return (
    <>
      <DashboardLayout>
        <Helmet>
          <title>Quản lý chức danh</title>
        </Helmet>
        <PositionHeader>
          <AppPageHeader
            title={'Quản lý Chức danh'}
            addon={
              <Space direction={'horizontal'}>
                <AccessControlAction
                  aclActionType={ACL_ACTION_TYPE.sort__CHUC_DANH}>
                  <Button
                    className="custom-ant-button icon-btn"
                    icon={<OrderedListOutlined />}
                    onClick={showSortPositionModal}>
                    Sắp xếp
                  </Button>
                </AccessControlAction>
                <AccessControlAction
                  aclActionType={ACL_ACTION_TYPE.create__CHUC_DANH}>
                  <Button
                    className="custom-ant-button icon-btn"
                    icon={<PlusOutlined />}
                    onClick={showCreatePositionModal}
                    type="primary">
                    Tạo chức danh
                  </Button>
                </AccessControlAction>
              </Space>
            }
          />
        </PositionHeader>
        <PositionContentWrapper>
          <ListPositionContent>
            <PositionLayout>
              <PositionNav>
                <PositionList>
                  {positionList.map(position => (
                    <Tooltip
                      title={position.name}
                      key={position.code}
                      placement="right">
                      <PositionButton
                        htmlType="button"
                        className={
                          currentPosition?.code === position.code
                            ? 'active'
                            : ''
                        }
                        onClick={() => onChangeTabPosition(position.code)}>
                        <span>{position.name}</span>
                      </PositionButton>
                    </Tooltip>
                  ))}
                </PositionList>
              </PositionNav>

              <PositionPanel>
                {currentPosition ? (
                  <>
                    <PositionPanelHeader>
                      <PositionTitleWrapper>
                        <PositionTitle>{currentPosition.name}</PositionTitle>
                        {userOfPositionListTotalCount !== undefined &&
                          userOfPositionListTotalCount !== null && (
                            <span className="count-badge">
                              {userOfPositionListTotalCount} nhân sự
                            </span>
                          )}
                      </PositionTitleWrapper>
                      <PositionActionGroup>
                        <AccessControlAction
                          aclActionType={ACL_ACTION_TYPE.edit__CHUC_DANH}>
                          <Tooltip title={'Chỉnh sửa chức danh'}>
                            <PositionActionButton
                              htmlType="button"
                              aria-label={'Chỉnh sửa chức danh'}
                              onClick={showEditPositionModal}>
                              <EditOutlined />
                            </PositionActionButton>
                          </Tooltip>
                        </AccessControlAction>
                        <AccessControlAction
                          aclActionType={ACL_ACTION_TYPE.delete__CHUC_DANH}>
                          <Tooltip
                            title={
                              deleteConfirmVisible ? '' : 'Xóa chức danh'
                            }>
                            <Popconfirm
                              visible={deleteConfirmVisible}
                              onVisibleChange={setDeleteConfirmVisible}
                              onConfirm={() => {
                                setDeleteConfirmVisible(false)
                                handleDelete()
                              }}
                              onCancel={() => setDeleteConfirmVisible(false)}
                              okText={'Đồng ý'}
                              cancelText={'Không'}
                              okType={'danger'}
                              placement="topRight"
                              overlayClassName="position-delete-popconfirm"
                              icon={
                                <QuestionCircleOutlined
                                  style={{ color: '#ef4444' }}
                                />
                              }
                              title={'Bạn có muốn xóa chức danh này không?'}>
                              <PositionActionButton
                                danger
                                htmlType="button"
                                aria-label={'Xóa chức danh'}>
                                <DeleteOutlined />
                              </PositionActionButton>
                            </Popconfirm>
                          </Tooltip>
                        </AccessControlAction>
                      </PositionActionGroup>
                    </PositionPanelHeader>

                    <Spin spinning={loadingAnimationStore.tableLoading}>
                      {userOfPositionData.length === 0 ? (
                        <EmptyUserWrapper>
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={'Không có người dùng'}
                          />
                        </EmptyUserWrapper>
                      ) : (
                        <>
                          <UserGrid>
                            {userOfPositionData.map(user => (
                              <PositionUserCard
                                key={user.code || user.username}
                                user={user}
                                onView={handleViewUserInfo}
                              />
                            ))}
                          </UserGrid>
                          <AppPaginationWrapper>
                            <AppPagination
                              onChange={onChangePagination}
                              total={userOfPositionListTotalCount}
                              pageSize={userOfPositionListPageSize}
                              current={userOfPositionListPageIndex + 1}
                            />
                          </AppPaginationWrapper>
                        </>
                      )}
                    </Spin>
                  </>
                ) : (
                  <EmptyUserWrapper>
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={'Chưa có chức danh nào'}
                    />
                  </EmptyUserWrapper>
                )}
              </PositionPanel>
            </PositionLayout>
          </ListPositionContent>
        </PositionContentWrapper>
      </DashboardLayout>

      <CreatePositonModal
        isVisibleCreatePositionModal={isVisibleCreatePositionModal}
        mode={mode}
        handleCloseCreatePositionModal={handleCloseCreatePositionModal}
        currentPosition={currentPosition}
        setCurrentPosition={setCurrentPosition}
        loadUserList={loadUserList}
      />

      <UserOverview
        isVisibleUser={isVisibleUser}
        handleCancelViewUser={handleCancelViewUser}
        selectedUser={userStore.selectedUser}
      />

      <SortPositionodal
        positionList={positionList}
        isVisibleSortPositionModal={isVisibleSortPositionModal}
        onCancel={handleCancelSortPositionModal}
        handleCancelSortPositionModal={handleCancelSortPositionModal}
        onRefreshListPosition={onRefreshListPosition}
      />
    </>
  )
}

PositionPage.propTypes = {}

export default memo(
  inject(
    'positionStore',
    'userStore',
    'loadingAnimationStore',
    'aclStore',
    'authenticationStore',
    'moduleStore'
  )(observer(PositionPage))
)
